<script setup lang="ts">
import { computed, ref } from 'vue'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'
import { getMercariUrl } from '@/lib/mercari'
import { getPageUrl } from '@/lib/getSeoRedirect'
import { fetchSettingBuyforyou } from '@/assets/ts/apiManagement'
import { isRegionSupportBuyforyou } from '@/lib/checkService'

defineProps({
	content: {
		type: Object,
		required: true,
	},
})

const isMercariEnabled = ref(false)
const isYahooEnabled = ref(false)
const icons = ['plane_v3', 'community_v3', 'online_store_v3']

const handleCardClick = (index: number) => {
	const ITEMS = [
		'Button A - Services Shortcut',
		'Button B - Services Shortcut',
		'Button C - Services Shortcut',
	]

	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.HomePage,
		button_name: ITEMS[index],
	})
}

const triangleSize = '12px'
const pageContent = computed(() => window.pageContent)
const items = computed(() => [
	...(isMercariEnabled.value
		? [
				{
					title: pageContent.value?.serviceEntry?.mercari || '',
					icon: 'mercari_logo',
					link: getMercariUrl('index'),
					buttonName: 'Mercari proxy order',
				},
		  ]
		: []),
	...(isYahooEnabled.value
		? [
				{
					title: pageContent.value?.serviceEntry?.yahoo || '',
					icon: 'yahoo',
					link: getPageUrl(GaGeneralPageName.YahooLanding),
					buttonName: 'JDirectItems Auction',
				},
		  ]
		: []),
])

async function checkIsServiceEnabled() {
	const { partners } = await fetchSettingBuyforyou()
	if (!partners) {
		isMercariEnabled.value = false
		isYahooEnabled.value = false
		return
	}
	if (partners.mercari.enabled === 'yes') {
		isMercariEnabled.value = true
	}
	if (partners.yahoo.enabled === 'yes') {
		isYahooEnabled.value = true
	}
}

const isServiceSectionShow = computed(() => {
	return (
		isRegionSupportBuyforyou.value &&
		(isMercariEnabled.value || isYahooEnabled.value)
	)
})

function sendServiceSectionGaEvent(name: string) {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.HomePage,
		button_name: name,
	})
}

if (isRegionSupportBuyforyou.value) {
	checkIsServiceEnabled()
}
</script>

<template>
	<div class="bs-container py-distant-acquaintances">
		<div class="col-span-12">
			<div
				class="mb-acquaintances flex flex-col items-center gap-close-friends text-center"
			>
				<div
					class="typography-h2-str text-n-950 [&>span]:text-o-500"
					v-html="content.title"
				></div>

				<div class="typography-b2-reg text-n-800">
					{{ content.description }}
				</div>
			</div>

			<div class="flex flex-col gap-20px mdp:flex-row lg:gap-24px">
				<div
					v-for="(item, index) in content.cards"
					:key="index"
					class="flex flex-1 flex-col gap-20px rounded-[12px] bg-o-50 p-20px mdp:gap-24px lg:p-24px"
				>
					<div class="flex items-center gap-12px">
						<BaseIcon :name="icons[index]" class="h-32px w-32px fill-o-500" />
						<div class="typography-h4-str text-n-950">
							{{ item.title }}
						</div>
					</div>

					<div class="flex flex-col gap-16px">
						<div
							v-for="(point, index) in item.pointItems"
							:key="index"
							class="flex gap-12px"
						>
							<BaseIcon name="success_v3" class="h-24px w-20px flex-shrink-0" />
							<div class="typography-s1-reg text-n-500">
								{{ point }}
							</div>
						</div>
					</div>

					<a
						:href="item.buttonLink"
						class="mt-auto"
						@click="handleCardClick(index)"
					>
						<BaseButton size="medium" class="w-full">
							{{ item.buttonText }}
						</BaseButton>
					</a>
				</div>
			</div>

			<div
				class="mt-20px flex flex-col items-center mdp:mt-32px"
				v-if="isServiceSectionShow"
			>
				<span
					:class="`h-0 w-0 border-b-[${triangleSize}] border-l-[${triangleSize}] border-r-[${triangleSize}] border-b-t-50 border-l-transparent border-r-transparent`"
				></span>
				<div
					class="flex w-full items-center justify-center gap-24px rounded-[12px] bg-t-50 p-24px mdp:gap-60px"
				>
					<a
						class="group inline-flex flex-wrap items-center justify-center"
						v-for="item in items"
						:key="item.icon"
						:href="item.link"
						@click="sendServiceSectionGaEvent(item.buttonName)"
					>
						<span
							class="mb-8px inline-flex h-[60px] w-[60px] items-center justify-center rounded-full bg-white mdp:mb-0 mdp:mr-20px"
						>
							<BaseIcon :name="item.icon" class="h-32px w-32px" />
						</span>
						<p
							class="typography-s1-emp basis-full text-center text-black group-hover:text-o-500 mdp:basis-auto mdp:text-start"
						>
							{{ item.title }}
						</p>
					</a>
				</div>
			</div>
		</div>
	</div>
</template>
